import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Calendar, FileText, Scale, Ruler, Camera, BarChart3, Dumbbell, Package, Plus, Trash2, Utensils } from 'lucide-react';
import { Client, ClientPackage, TrainingPackage, PackageUsageStats } from '../types';
import { formatPhoneDisplay } from '../utils/formatPhone';
import { packagesApi } from '../services/packages';
import { usePackages } from '../hooks/usePackages';
import { toast } from 'react-hot-toast';

interface ViewClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client | null;
  onWeightClick?: () => void;
  onMeasurementsClick?: () => void;
  onPhotoClick?: () => void;
  onStatsClick?: () => void;
  onNutritionClick?: () => void;
  onWorkoutClick?: () => void;
}

const ViewClientModal: React.FC<ViewClientModalProps> = ({
  isOpen,
  onClose,
  client,
  onWeightClick,
  onMeasurementsClick,
  onPhotoClick,
  onStatsClick,
  onNutritionClick,
  onWorkoutClick
}) => {
  const [clientPackages, setClientPackages] = useState<ClientPackage[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedPackageForStats, setSelectedPackageForStats] = useState<ClientPackage | null>(null);
  const [packageStats, setPackageStats] = useState<PackageUsageStats[]>([]);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const { packages: allPackages, isLoading: packagesLoading } = usePackages();

  // Загрузка пакетов клиента
  useEffect(() => {
    if (client?.id) {
      loadClientPackages();
    }
  }, [client?.id]);

  const loadClientPackages = async () => {
    if (!client?.id) return;
    try {
      const packages = await packagesApi.getClientPackagesHistory(client.id, 10);
      setClientPackages(packages);
    } catch (error) {
      console.error('Ошибка загрузки пакетов клиента:', error);
    }
  };

  const handleAssignPackage = async () => {
    if (!selectedPackageId || !client?.id) return;

    setIsAssigning(true);
    try {
      await packagesApi.assignPackageToClient(selectedPackageId, {
        client_id: client.id,
        package_id: selectedPackageId,
      });
      setSelectedPackageId(null);
      toast.success('Пакет успешно назначен клиенту');
      await loadClientPackages(); // Перезагружаем список пакетов
    } catch (error) {
      console.error('Ошибка назначения пакета:', error);
      toast.error('Ошибка при назначении пакета');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUnassignPackage = async (clientPackageId: number, packageName: string) => {
    // Показываем toast с подтверждением
    toast((t) => (
      <div className="flex flex-col space-y-2">
        <div className="font-medium">Отвязать пакет "{packageName}"?</div>
        <div className="text-sm text-gray-600">Это также удалит все связи с тренировками</div>
        <div className="flex space-x-2">
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                const result = await packagesApi.unassignPackageFromClient(clientPackageId);
                toast.success(`${result.message}. Удалено связей: ${result.deleted_workout_links}`);
                await loadClientPackages(); // Перезагружаем список пакетов
              } catch (error: any) {
                console.error('Ошибка отвязывания пакета:', error);
                toast.error(error.response?.data?.detail || 'Ошибка при отвязывании пакета');
              }
            }}
            className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
          >
            Удалить
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-3 py-1 rounded text-sm"
          >
            Отмена
          </button>
        </div>
      </div>
    ), {
      duration: 6000,
      position: 'top-center',
    });
  };

  const handleShowStats = async (clientPackage: ClientPackage) => {
    setSelectedPackageForStats(clientPackage);
    setIsLoadingStats(true);
    try {
      const stats = await packagesApi.getPackageUsageStats(clientPackage.id);
      setPackageStats(stats);
    } catch (error: any) {
      console.error('Ошибка загрузки статистики пакета:', error);
      toast.error('Ошибка при загрузке статистики пакета');
    } finally {
      setIsLoadingStats(false);
    }
  };

  const getPackageStatusColor = (clientPackage: ClientPackage): string => {
    if (!clientPackage.is_active || clientPackage.is_expired) {
      return 'text-red-600 bg-red-50';
    }
    return 'text-green-600 bg-green-50';
  };

  const getPackageStatusText = (clientPackage: ClientPackage): string => {
    if (!clientPackage.is_active) return 'Завершен';
    if (clientPackage.is_expired) return 'Истек';
    if ((clientPackage.sessions_remaining ?? 0) === 0) return 'Исчерпан';
    return 'Активен';
  };

  if (!isOpen || !client) return null;

  // Функция для расчета возраста
  const calculateAge = (dateOfBirth?: string) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Заголовок */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Информация о клиенте</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>
        {/* Контент */}
        <div className="p-6 space-y-6">
          <div className="flex items-center space-x-4">
            <div className="bg-blue-100 rounded-full p-3">
              <User size={32} className="text-blue-600" />
            </div>
            <div>
              <div className="text-lg font-bold text-gray-900">{client.first_name} {client.last_name}</div>
              <div className="text-sm text-gray-500">ID: {client.id}</div>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center text-sm text-gray-600">
              <Mail size={16} className="mr-2 text-gray-400" />
              {client.email}
            </div>
            {client.phone && (
              <div className="flex items-center text-sm text-gray-600">
                <Phone size={16} className="mr-2 text-gray-400" />
                {formatPhoneDisplay(client.phone)}
              </div>
            )}
            {client.date_of_birth && (
              <div className="flex items-center text-sm text-gray-600">
                <Calendar size={16} className="mr-2 text-gray-400" />
                {new Date(client.date_of_birth).toLocaleDateString('ru-RU')} 
                {calculateAge(client.date_of_birth) && (
                  <span className="ml-2">({calculateAge(client.date_of_birth)} лет)</span>
                )}
              </div>
            )}
            {client.notes && (
              <div className="flex items-start text-sm text-gray-600">
                <FileText size={16} className="mr-2 text-gray-400 mt-1" />
                <span>{client.notes}</span>
              </div>
            )}
            <div className="flex items-center text-sm">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${client.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {client.is_active ? 'Активен' : 'Неактивен'}
              </span>
            </div>
          </div>
          
          {/* Иконки действий */}
          <div className="flex items-center justify-center space-x-4 pt-4 border-t border-gray-200">
            <div className="cursor-pointer hover:opacity-70 transition-opacity" onClick={onWeightClick}>
              <div className="bg-emerald-100 rounded-full p-2">
                <Scale size={16} className="text-emerald-600" />
              </div>
            </div>

            <div className="cursor-pointer hover:opacity-70 transition-opacity" onClick={onMeasurementsClick}>
              <div className="bg-orange-100 rounded-full p-2">
                <Ruler size={16} className="text-orange-600" />
              </div>
            </div>

            <div className="cursor-pointer hover:opacity-70 transition-opacity" onClick={onPhotoClick}>
              <div className="bg-pink-100 rounded-full p-2">
                <Camera size={16} className="text-pink-600" />
              </div>
            </div>

            <div className="cursor-pointer hover:opacity-70 transition-opacity" onClick={onStatsClick}>
              <div className="bg-purple-100 rounded-full p-2">
                <BarChart3 size={16} className="text-purple-600" />
              </div>
            </div>

            <div className="cursor-pointer hover:opacity-70 transition-opacity" onClick={onNutritionClick}>
              <div className="bg-green-100 rounded-full p-2">
                <Utensils size={16} className="text-green-600" />
              </div>
            </div>

            <div className="cursor-pointer hover:opacity-70 transition-opacity" onClick={onWorkoutClick}>
              <div className="bg-blue-100 rounded-full p-2">
                <Dumbbell size={16} className="text-blue-600" />
              </div>
            </div>
          </div>

          {/* Секция пакетов */}
          <div className="pt-6 border-t border-gray-200">
            <div className="mb-3">
              <div className="flex items-center mb-2">
                <Package size={18} className="text-indigo-600 mr-2" />
                <h3 className="text-base font-semibold text-gray-900">Пакеты тренировок</h3>
              </div>
              {/* Кнопка назначения пакета */}
              <div className="flex items-center space-x-2">
                <select
                  value={selectedPackageId || ''}
                  onChange={(e) => setSelectedPackageId(e.target.value ? Number(e.target.value) : null)}
                  className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  disabled={packagesLoading}
                >
                  <option value="">Выберите пакет</option>
                  {allPackages?.map(pkg => (
                    <option key={pkg.id} value={pkg.id}>
                      {pkg.name} ({pkg.sessions_count} тр.)
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAssignPackage}
                  disabled={!selectedPackageId || isAssigning}
                  className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white px-2 py-1 rounded-md flex items-center text-xs font-medium transition-colors whitespace-nowrap"
                >
                  <Plus size={14} className="mr-1" />
                  {isAssigning ? 'Назначение...' : 'Назначить'}
                </button>
              </div>
            </div>

            {/* История пакетов */}
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {clientPackages.length === 0 ? (
                <div className="text-gray-500 text-center py-4">
                  Пакеты не назначены
                </div>
              ) : (
                clientPackages.map(clientPackage => (
                  <div key={clientPackage.id} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">
                        {clientPackage.package?.name}
                      </h4>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPackageStatusColor(clientPackage)}`}>
                          {getPackageStatusText(clientPackage)}
                        </span>
                        <button
                          onClick={() => handleShowStats(clientPackage)}
                          className="text-blue-500 hover:text-blue-700 p-1 rounded transition-colors"
                          title="Показать статистику использования пакета"
                        >
                          <BarChart3 size={16} />
                        </button>
                        <button
                          onClick={() => handleUnassignPackage(clientPackage.id, clientPackage.package?.name || 'Пакет')}
                          className="text-red-500 hover:text-red-700 p-1 rounded transition-colors"
                          title="Отвязать пакет от клиента"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>
                        Использовано: {clientPackage.sessions_used} из {clientPackage.package?.sessions_count || 0}
                      </span>
                      <span>
                        Осталось: {clientPackage.sessions_remaining ?? 0}
                      </span>
                    </div>
                    {clientPackage.expiry_date && (
                      <div className="text-xs text-gray-500 mt-1">
                        Действителен до: {new Date(clientPackage.expiry_date).toLocaleDateString('ru-RU')}
                      </div>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                      Назначен: {new Date(clientPackage.created_at).toLocaleDateString('ru-RU')}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Модальное окно статистики пакета */}
      {selectedPackageForStats && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              {/* Заголовок */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <BarChart3 size={20} className="text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Статистика использования пакета
                  </h3>
                </div>
                <button
                  onClick={() => setSelectedPackageForStats(null)}
                  className="text-gray-400 hover:text-gray-600 p-1 rounded"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Информация о пакете */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-gray-900 mb-2">
                  {selectedPackageForStats.package?.name}
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Использовано:</span>
                    <div className="font-medium">{selectedPackageForStats.sessions_used}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Осталось:</span>
                    <div className="font-medium">{selectedPackageForStats.sessions_remaining ?? 0}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Всего:</span>
                    <div className="font-medium">{selectedPackageForStats.package?.sessions_count || 0}</div>
                  </div>
                </div>
              </div>

              {/* История использования */}
              <div>
                <h5 className="font-medium text-gray-900 mb-3">История использования</h5>
                {isLoadingStats ? (
                  <div className="text-center py-8 text-gray-500">Загрузка...</div>
                ) : packageStats.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Пакет еще не использовался
                  </div>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {packageStats.map((stat, index) => (
                      <div key={index} className="bg-white border rounded-lg p-3">
                        <div>
                          <div className="font-medium text-gray-900">
                            {stat.workout_name || `Тренировка #${stat.workout_id}`}
                          </div>
                          <div className="text-sm text-gray-600">
                            {new Date(stat.workout_date).toLocaleDateString('ru-RU', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              weekday: 'long'
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewClientModal; 